import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useUIStore } from '@/stores/ui.store';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { isRtlLocale } from '@/lib/locale-direction';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Footer } from './Footer';
import { GlobalSearch } from './GlobalSearch';
import { QuickActions } from './QuickActions';
import { InstitutionAppThemeLayout } from './InstitutionAppTheme';
import { TrainerTourRunner } from '@/features/trainer-tour/TrainerTourRunner';
import A11yToolbar from '@/components/accessibility/A11yToolbar';

function SkipLinks() {
  const { t } = useTranslation();

  return (
    <nav
      aria-label={t('a11y.skipLinksLandmark', 'Quick access links')}
      className="sr-only focus-within:not-sr-only focus-within:fixed focus-within:z-[100] focus-within:top-0 focus-within:start-0"
    >
      <a
        href="#main-content"
        className="fixed start-0 top-0 z-[100] -translate-y-full rounded-ee-lg bg-[color:var(--inst-player-primary,#0d9488)] px-4 py-2 text-sm font-medium text-white transition-transform focus:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0d9488)_55%,white)]"
      >
        {t('a11y.skipToContent', 'Skip to main content')}
      </a>
      <a
        href="#main-nav"
        className="fixed top-0 z-[100] -translate-y-full rounded-ee-lg bg-[color:var(--inst-player-primary,#0d9488)] px-4 py-2 text-sm font-medium text-white transition-transform start-48 focus:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0d9488)_55%,white)]"
      >
        {t('a11y.skipToNav', 'Skip to navigation')}
      </a>
    </nav>
  );
}

export function AppLayout() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const { sidebarCollapsed, sidebarOpen, commandPaletteOpen, quickActionsOpen, setSidebarCollapsed, toggleSidebar } = useUIStore();
  const mainLang = i18n.language?.split('-')[0] ?? 'fr';
  const isRtl = isRtlLocale(i18n.language);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && sidebarOpen) {
        toggleSidebar();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen, toggleSidebar]);

  useEffect(() => {
    // Defensive cleanup: if an external help/embed overlay stays mounted by mistake,
    // it can keep a full-screen backdrop and block all clicks on dashboard pages.
    const staleEmbedOverlay = document.getElementById('lyrra-eduflow-embed-root');
    if (staleEmbedOverlay?.parentNode) {
      staleEmbedOverlay.parentNode.removeChild(staleEmbedOverlay);
      document.body.style.overflow = '';
    }
  }, [pathname]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'b' && (e.metaKey || e.ctrlKey) && e.shiftKey) {
        e.preventDefault();
        setSidebarCollapsed(!sidebarCollapsed);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sidebarCollapsed, setSidebarCollapsed]);

  const sidebarWidth = sidebarCollapsed ? 64 : 240;

  return (
    <InstitutionAppThemeLayout>
      <TrainerTourRunner />

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      <div
        className="flex min-h-0 min-w-0 flex-1 flex-row overflow-hidden"
        dir={isRtl ? 'rtl' : 'ltr'}
        lang={mainLang}
      >
        <SkipLinks />
        {/* Sidebar : bord « start » (gauche en LTR, droite en RTL) ; hors écran mobile selon la direction */}
        <aside
          id="main-nav"
          className={cn(
            'fixed inset-y-0 start-0 z-40 flex-shrink-0 transition-transform duration-300 md:relative md:translate-x-0',
            sidebarOpen
              ? 'max-md:translate-x-0'
              : isRtl
                ? 'max-md:translate-x-full'
                : 'max-md:-translate-x-full',
          )}
          style={{ width: sidebarWidth }}
        >
          <Sidebar />
        </aside>

        {/* Main area */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Header />

          <main
            id="main-content"
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-[color:var(--inst-player-bg,#fdfbf7)]',
              'p-4 md:p-6 lg:p-8',
            )}
          >
            <Outlet />
          </main>

          <Footer />
        </div>
      </div>

      {/* Overlays */}
      {commandPaletteOpen && <GlobalSearch />}
      {quickActionsOpen && <QuickActions />}

      <A11yToolbar
        key={`trainer-a11y-${i18n.resolvedLanguage ?? i18n.language ?? 'fr'}`}
        fabPosition="left"
        panelPosition="left"
      />
    </InstitutionAppThemeLayout>
  );
}

export default AppLayout;
